import './BundleDetails.scss';

import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCol,
  IonGrid,
  IonIcon,
  IonItem,
  IonLabel,
  IonRow,
} from '@ionic/react';
import { informationCircle } from 'ionicons/icons';
import React, { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { assertNever } from 'src/utils/assert';
import { isValidBundlePattern } from 'src/utils/helpers/bundleParserHelpers';
import { getDate, getOrgName } from 'src/utils/helpers/referenceHelpers';
import { EntityPermission } from '../../constants';
import { BundleMessages } from '../../intl/BundleMessages';
import { GenericMessages } from '../../intl/GenericMessages';
import { Bundle } from '../../models/bundle';
import { Organization } from '../../models/organization';
import { BundleTranslator } from '../../utils/helpers/BundleTranslator';
import { useHasTouchscreen } from '../../utils/helpers/platformHelpers';
import { DqText } from '../dq-text';
import { BundleChart } from './BundleChart';

export interface BundleDetailProps {
  bundle: Bundle;
  organizations: Organization[];
}

export const BundleDetails: FC<BundleDetailProps> = ({ bundle, organizations }) => {
  const { formatMessage } = useIntl();

  const patternDisplay = useMemo(() => {
    try {
      switch (bundle.pattern) {
        case 'square':
          return formatMessage(BundleMessages.patternSquare);
        case 'triangle':
          return formatMessage(BundleMessages.patternTriangle);
        default:
          assertNever(bundle.pattern);
      }
    } catch {
      return undefined;
    }
  }, [bundle.pattern, formatMessage]);

  const transformedTubes = React.useMemo(() => {
    return BundleTranslator.transformTubes(bundle);
  }, [bundle]);

  const buildSharedBundleList = (permissions: EntityPermission[]) => {
    if (!permissions || permissions.length === 0) {
      return formatMessage(BundleMessages.sharedBundlesDefaultLabel);
    }
    return permissions.map(permission => permission.principalName).join(', ');
  };

  return (
    <>
      <IonCard className="bundle-detail-card">
        <IonCardContent>
          <IonGrid className="bundle-detail-grid">
            <IonRow>
              <IonCol size="6" sizeSm="4" sizeMd="3" sizeXl="2">
                <DqText color="medium" variant="body-small">
                  {formatMessage(BundleMessages.organizationLabel)}
                </DqText>
                <DqText variant="body-strong">
                  {getOrgName(bundle.organizationId, organizations ?? []) ?? GenericMessages.unknownPlaceholder}
                </DqText>
              </IonCol>
              <IonCol size="6" sizeSm="4" sizeMd="3" sizeXl="2">
                <DqText color="medium" variant="body-small">
                  {formatMessage(BundleMessages.siteNameLabel)}
                </DqText>
                <DqText variant="body-strong">{bundle.siteName || '-'}</DqText>
              </IonCol>
              <IonCol size="6" sizeSm="4" sizeMd="3" sizeXl="2">
                <DqText color="medium" variant="body-small">
                  {formatMessage(BundleMessages.uploadDateLabel)}
                </DqText>
                <DqText variant="body-strong">{getDate(bundle.createdAt) || '-'}</DqText>
              </IonCol>
              <IonCol size="6" sizeSm="4" sizeMd="3" sizeXl="2">
                <DqText color="medium" variant="body-small">
                  {formatMessage(BundleMessages.patternLabel)}
                </DqText>
                <DqText variant="body-strong">{patternDisplay || '-'}</DqText>
              </IonCol>
              <IonCol size="6" sizeSm="4" sizeMd="3" sizeXl="2">
                <DqText color="medium" variant="body-small">
                  {formatMessage(BundleMessages.rotatedLabel)}
                </DqText>
                <DqText variant="body-strong">
                  {bundle.rotated
                    ? formatMessage(GenericMessages.yesButtonLabel)
                    : formatMessage(GenericMessages.noButtonLabel)}
                </DqText>
              </IonCol>
              <IonCol size="6" sizeSm="4" sizeMd="3" sizeXl="2">
                <DqText color="medium" variant="body-small">
                  {formatMessage(BundleMessages.modelNumberLabel)}
                </DqText>
                <DqText variant="body-strong">{bundle.heatExchangerModelNumber || '-'}</DqText>
              </IonCol>
              <IonCol size="6" sizeSm="4" sizeMd="3" sizeXl="2">
                <DqText color="medium" variant="body-small">
                  {formatMessage(BundleMessages.assetNumberLabel)}
                </DqText>
                <DqText variant="body-strong">{bundle.assetNumber || '-'}</DqText>
              </IonCol>
              <IonCol size="6" sizeSm="4" sizeMd="3" sizeXl="2">
                <DqText color="medium" variant="body-small">
                  {formatMessage(BundleMessages.tubeCountLabel)}
                </DqText>
                <DqText variant="body-strong">{bundle.tubeCount || '-'}</DqText>
              </IonCol>
              <IonCol size="6" sizeSm="4" sizeMd="3" sizeXl="2">
                <DqText color="medium" variant="body-small">
                  {formatMessage(BundleMessages.tubeLengthLabel)}
                </DqText>
                <DqText variant="body-strong">
                  {bundle.tubeLength || '-'}&nbsp;{bundle.tubeLengthUnit}
                </DqText>
              </IonCol>
              <IonCol size="6" sizeSm="4" sizeMd="3" sizeXl="2">
                <DqText color="medium" variant="body-small">
                  {formatMessage(BundleMessages.tubeDiameterLabel)}
                </DqText>
                <DqText variant="body-strong">
                  {bundle.tubeDiameter || '-'}&nbsp;{bundle.tubeDiameterUnit}
                </DqText>
              </IonCol>
              <IonCol size="6" sizeSm="4" sizeMd="3" sizeXl="2">
                <DqText color="medium" variant="body-small">
                  {formatMessage(BundleMessages.pitchLabel)}
                </DqText>
                <DqText variant="body-strong">
                  {bundle.pitch || '-'}&nbsp;{bundle.pitchUnit}
                </DqText>
              </IonCol>
              <IonCol size="12" sizeMd="6">
                <DqText color="medium" variant="body-small">
                  {formatMessage(BundleMessages.bundleInfoSharedWithLabel)}
                </DqText>
                <DqText variant="body-strong">{buildSharedBundleList(bundle?.permissions ?? [])}</DqText>
              </IonCol>
              <IonCol size="12" sizeMd="6">
                <DqText color="medium" variant="body-small">
                  {formatMessage(BundleMessages.notesLabel)}
                </DqText>
                <DqText variant="body-strong">{bundle.description || '-'}</DqText>
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonCardContent>
      </IonCard>

      {isValidBundlePattern(bundle.pattern) && (
        <IonCard className="bundle-detail-card ion-padding-top">
          <IonCardHeader>
            <IonRow className="ion-justify-content-between ion-align-items-center ion-nowrap">
              <DqText variant="h2" className="ion-text-nowrap">
                {formatMessage(BundleMessages.bundlePreviewTitle)}
              </DqText>
              <BundleChartHelp />
            </IonRow>
          </IonCardHeader>
          <IonCardContent className="bundle-card-chart">
            <BundleChart
              key={bundle._id}
              tubes={transformedTubes}
              pattern={bundle.pattern}
              isRotated={bundle.rotated}
            />
          </IonCardContent>
        </IonCard>
      )}
    </>
  );
};

const BundleChartHelp: FC = () => {
  const { formatMessage } = useIntl();
  const hasTouchscreen = useHasTouchscreen();

  return (
    <IonItem lines="none">
      <IonIcon icon={informationCircle} color="primary" className="ion-margin-end" />
      <IonLabel>
        {hasTouchscreen ? (
          <DqText variant="body-strong" className="ion-text-wrap">
            {formatMessage(BundleMessages.bundleChartInfoMobile)}
          </DqText>
        ) : (
          <DqText variant="body-strong" className="ion-text-wrap">
            {formatMessage(BundleMessages.bundleChartInfoWeb)}
          </DqText>
        )}
      </IonLabel>
    </IonItem>
  );
};
